import { FitAddon } from "@xterm/addon-fit";
import { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";
import { CRLF, RESET } from "../shared/ansi";
import {
  HEARTBEAT, PROTOCOL_VERSION, type ClientFrame, type Ctx, type InputMode, type ServerEvent, type ServerFrame, type VaultOp,
} from "../shared/protocol";
import type { Skin } from "../shared/skin";
import { Renderer } from "./render";
import { Sound } from "./sound";
import { VaultSession } from "./vault";

// Colors are the CGA palette, which is what the boards of 1993 actually looked like.
const CGA = {
  background: "#000000", foreground: "#aaaaaa", cursor: "#aaaaaa",
  black: "#000000", red: "#aa0000", green: "#00aa00", yellow: "#aa5500",
  blue: "#0000aa", magenta: "#aa00aa", cyan: "#00aaaa", white: "#aaaaaa",
  brightBlack: "#555555", brightRed: "#ff5555", brightGreen: "#55ff55", brightYellow: "#ffff55",
  brightBlue: "#5555ff", brightMagenta: "#ff55ff", brightCyan: "#55ffff", brightWhite: "#ffffff",
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Which line to dial, from the page's address: the Heap at /, a board at /b/<name>, the studio at /new. */
function line(): string {
  const board = /^\/b\/([a-z0-9][a-z0-9-]{1,30})\/?$/.exec(location.pathname)?.[1];
  if (board) return `/b/${board}/ws`;
  if (/^\/new\/?$/.test(location.pathname)) return `/new/ws?s=${studioSession()}`;
  return "/ws";
}

/** One studio session per browser, so coming back resumes the same conversation and board. */
function studioSession(): string {
  let id: string | null = null;
  try {
    id = localStorage.getItem("atdt.studio");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("atdt.studio", id);
    }
  } catch {
    id ??= crypto.randomUUID();
  }
  return id;
}

type Prompt = Extract<ServerEvent, { t: "prompt" }>;
const lastPrompt = (events: ServerEvent[]): Prompt | undefined =>
  events.filter((e): e is Prompt => e.t === "prompt").at(-1);

/**
 * One call to the board. The board decides what to ask for; this collects it.
 * Between sending input and the board's reply, keystrokes queue up and replay
 * once the next prompt arrives, so fast typists lose nothing.
 *
 * The socket opens straight away while the dial plays; frames that arrive early
 * wait for CONNECT, so the handshake covers the connection time.
 */
class Call {
  private ws: WebSocket;
  private renderer?: Renderer;
  private mode: InputMode | "wait" | "off" = "wait";
  private prompt: { input: InputMode; max: number } = { input: "line", max: 60 };
  /** The live prompt as drawn, so it can be redrawn under pushed lines. */
  private promptText = "";
  private buf = "";
  private queued = "";

  private connected = false;
  private vault?: VaultSession;
  private skin?: Skin;
  private lastCtx: Ctx = { node: 0 };
  /** Waiting for replies to vault requests, oldest first. */
  private replies: ((events: ServerEvent[]) => void)[] = [];
  private early: ServerFrame[] = [];
  private closedWith: CloseEvent | null = null;

  constructor(private term: Terminal, private sound: Sound, private onEnd: () => void) {
    const proto = location.protocol === "https:" ? "wss" : "ws";
    this.ws = new WebSocket(`${proto}://${location.host}${line()}`);
    let heartbeat = 0;
    let skinArrived: (skin: Skin | undefined) => void = () => {};
    const hello = new Promise<Skin | undefined>((r) => (skinArrived = r));

    this.ws.onopen = () => {
      heartbeat = window.setInterval(() => this.ws.send(HEARTBEAT.ping), HEARTBEAT.everyMs);
    };
    this.ws.onmessage = (e) => {
      if (e.data === HEARTBEAT.pong) return;
      const frame = JSON.parse(e.data) as ServerFrame;
      if (this.connected) return this.receive(frame);
      this.early.push(frame);
      const h = frame.events.find((ev) => ev.t === "hello");
      if (h && h.t === "hello") skinArrived(h.skin);
    };
    this.ws.onclose = (e) => {
      clearInterval(heartbeat);
      this.closedWith = e;
      skinArrived(undefined);
      if (this.connected) this.hangUp(e);
    };
    this.place(hello);
  }

  /** Dial, ring (or hear busy), handshake, then let the board's frames through. */
  private async place(hello: Promise<Skin | undefined>) {
    const { term, sound } = this;
    const skin = await Promise.race([hello, sleep(4000).then(() => undefined)]);
    sound.configure(skin?.sound);
    const number = skin?.phone ?? "";
    term.write(`\x1b[0;37mATDT ${number}\r\n`);
    await sound.dial(number);
    const busy = this.early.some((f) => f.events.some((e) => e.t === "screen" && e.name === "busy"));
    if (busy) {
      await sound.busy();
    } else if (skin) {
      term.write("\x1b[90mRINGING...\x1b[0m\r\n");
      await sound.ring();
      await sound.handshake();
      term.write("\x1b[1;37mCONNECT 14400/ARQ/V32BIS/LAPM\x1b[0m\r\n");
    }
    this.connected = true;
    for (const frame of this.early.splice(0)) this.receive(frame);
    if (this.closedWith) this.hangUp(this.closedWith);
  }

  /** Called once per call: from place() if the line dropped while dialing, otherwise from onclose. */
  private hangUp(e: CloseEvent) {
    this.mode = "off";
    this.term.write(`\r\n\x1b[0;37m${e.code === 1013 ? "BUSY" : "NO CARRIER"}\x1b[0m\r\n`);
    this.term.write("\r\n\x1b[90mPress any key to redial.\x1b[0m\r\n");
    this.onEnd();
  }

  keys(data: string) {
    // Arrow keys and other escape sequences have no meaning here yet.
    for (const ch of data.replace(/\x1b(\[[0-9;?]*[ -/]*[@-~]|O.|.)?/g, "")) {
      if (this.mode === "off") return;
      if (this.mode === "wait") {
        this.queued += ch;
      } else if (this.mode === "key") {
        this.send({ v: PROTOCOL_VERSION, t: "key", key: ch });
      } else if (ch === "\r" && this.mode === "chat" && this.buf.trim() === "/clear") {
        // Yours alone: wipe the screen and start the prompt fresh.
        this.buf = "";
        this.term.write("\x1b[2J\x1b[H" + this.promptText);
      } else if (ch === "\r" && this.mode === "sealed") {
        // Vault lines never go up as text: the vault session encrypts them, or handles the command.
        const line = this.buf;
        this.term.write(this.clearInput() + this.promptText);
        this.buf = "";
        this.vault?.line(line).then((left) => {
          if (left) this.vault = undefined;
        });
      } else if (ch === "\r") {
        // In chat the board echoes the line back as a chat line, so clear what was typed instead.
        this.term.write(this.mode === "chat" ? this.clearInput() : RESET + CRLF);
        this.send({ v: PROTOCOL_VERSION, t: "line", text: this.buf });
        this.buf = "";
      } else if (ch === "\x7f" || ch === "\b") {
        if (this.buf) {
          this.buf = this.buf.slice(0, -1);
          this.term.write("\b \b");
        }
      } else if (ch >= " " && this.buf.length < this.prompt.max) {
        this.buf += ch;
        this.term.write(this.mode === "secret" ? "*" : ch);
      }
    }
  }

  private send(frame: ClientFrame) {
    this.mode = "wait";
    this.ws.send(JSON.stringify(frame));
  }

  /** A vault op whose reply the vault session needs. Keystrokes queue until it arrives. */
  private request(op: VaultOp): Promise<ServerEvent[]> {
    return new Promise((resolve) => {
      this.replies.push(resolve);
      this.send({ v: PROTOCOL_VERSION, t: "vault", ...op });
    });
  }

  /** Print above the prompt, keeping whatever's being typed. */
  private printAbove(out: string) {
    // In the vault the prompt stays drawn while a request is out, so redraw it then too.
    const typing = this.mode === "wait" ? this.prompt.input === "sealed" : this.mode !== "off" && this.mode !== "key";
    if (!typing) {
      this.term.write(out);
    } else {
      const shown = this.mode === "secret" ? "*".repeat(this.buf.length) : this.buf;
      this.term.write(this.clearInput() + out + this.promptText + shown);
    }
  }

  private startVault(ctx: Ctx) {
    if (!this.renderer || !this.skin || !ctx.handle) return;
    const key = `atdt.vault.${location.host}.${this.skin.id}.${ctx.handle.toLowerCase()}`;
    this.vault = new VaultSession(
      {
        request: (op) => this.request(op), print: (s) => this.printAbove(s),
        renderer: this.renderer, ctx: () => this.lastCtx, storage: localStorage,
      },
      key, ctx.handle,
    );
    this.vault.enter();
  }

  /** Escape codes that erase the prompt and whatever's been typed, across wrapped rows. */
  private clearInput(): string {
    const visible = this.promptText.replace(/\x1b\[[0-9;]*m/g, "").length + this.buf.length;
    const rows = Math.max(1, Math.ceil(visible / this.term.cols));
    return "\r" + (rows > 1 ? `\x1b[${rows - 1}A` : "") + "\x1b[J";
  }

  /** Lines the board sent unprompted: draw them above the prompt, then redraw the prompt and typing. */
  private pushed(frame: ServerFrame) {
    if (!this.renderer) return;
    this.lastCtx = frame.ctx;
    for (const e of frame.events) if (e.t === "vault") this.vault?.onPush(e);
    const me = frame.ctx.handle?.toLowerCase();
    for (const e of frame.events) {
      if (e.t !== "chat") continue;
      if (e.line.kind === "join" && e.line.handle.toLowerCase() !== me) this.sound.chime();
      if (e.line.kind === "msg" && e.line.to?.toLowerCase() === me) this.sound.blip();
    }
    const prompt = lastPrompt(frame.events);
    const typing = this.mode !== "wait" && this.mode !== "off" && this.mode !== "key";
    const out = this.renderer.frame(frame.ctx, frame.events);
    if (prompt) {
      // The board moved us somewhere else (a kick); whatever was being typed no longer applies.
      this.term.write((typing ? this.clearInput() : "") + out);
      this.prompt = { input: prompt.input, max: prompt.max };
      this.promptText = this.renderer.frame(frame.ctx, [prompt]);
      this.buf = "";
      if (this.mode !== "wait") this.mode = prompt.input;
      return;
    }
    if (this.mode === "wait") {
      this.term.write(out);
    } else {
      const shown = this.mode === "secret" ? "*".repeat(this.buf.length) : this.buf;
      this.term.write(this.clearInput() + out + this.promptText + shown);
    }
  }

  private receive(frame: ServerFrame) {
    if (frame.push) return this.pushed(frame);
    for (const e of frame.events) {
      if (e.t === "hello") {
        if (e.protocol !== PROTOCOL_VERSION) {
          this.term.write(`\r\nThis board speaks protocol ${e.protocol}; reload the page.\r\n`);
          this.ws.close();
          return;
        }
        this.renderer = new Renderer(e.skin);
        this.skin = e.skin;
      }
      if (e.t === "prompt") this.prompt = { input: e.input, max: e.max };
    }
    if (!this.renderer) return;
    const prompt = lastPrompt(frame.events);
    if (prompt) this.promptText = this.renderer.frame(frame.ctx, [prompt]);
    this.lastCtx = frame.ctx;
    // Every client frame gets one reply, and in the vault every frame we send is a vault request.
    const answer = this.replies.shift();
    const staying = answer && prompt?.input === "sealed";
    // A vault reply that keeps us in the vault: the prompt is already on screen, so draw nothing.
    if (!staying) this.term.write(this.renderer.frame(frame.ctx, frame.events));

    if (frame.events.some((e) => e.t === "bye")) {
      this.mode = "off";
      return;
    }
    answer?.(frame.events);
    const entering = this.prompt.input === "sealed" && !this.vault && prompt?.input === "sealed";
    if (this.prompt.input !== "sealed") this.vault = undefined;
    // Every input gets one reply, so the live prompt is armed again either way.
    this.mode = this.prompt.input;
    if (entering) this.startVault(frame.ctx);
    const queued = this.queued;
    this.queued = "";
    if (queued) this.keys(queued);
  }
}

async function boot() {
  await document.fonts.load('16px "IBM Plex Mono"').catch(() => {});
  const term = new Terminal({
    theme: CGA, fontFamily: '"IBM Plex Mono", Menlo, monospace', fontSize: 16,
    cursorBlink: true, scrollback: 2000,
  });
  const fit = new FitAddon();
  term.loadAddon(fit);
  term.open(document.getElementById("screen")!);
  fit.fit();
  addEventListener("resize", () => fit.fit());
  term.focus();

  const sound = new Sound();
  const toggle = document.getElementById("sound") as HTMLButtonElement;
  const label = () => (toggle.textContent = `sound ${sound.on ? "on" : "off"}`);
  label();
  toggle.addEventListener("click", () => {
    sound.toggle();
    label();
    term.focus();
  });

  let call: Call | null = null;
  let dialing = false;

  // A keypress to dial, like typing ATDT, and the gesture browsers require before any sound.
  const dial = () => {
    dialing = true;
    sound.unlock();
    call = new Call(term, sound, () => {
      call = null;
      dialing = false;
    });
  };

  term.onData((data) => {
    if (call) call.keys(data);
    else if (!dialing) dial();
  });
  term.write("\x1b[90mPress any key to dial.\x1b[0m\r\n\r\n");
}

boot();
